/**
 * @fileoverview Dammen (Checkers) game types
 * @description Type definitions for the Dammen game
 */

/**
 * Player identifier - uses Opa/Oma SVG characters
 */
export type PlayerId = "opa" | "oma";

/**
 * Piece type - normal or king
 */
export type PieceType = "normal" | "king";

/**
 * A piece on the board
 */
export interface DammenPiece {
  owner: PlayerId;
  type: PieceType;
}

/**
 * Cell on the board (null = empty, or contains a piece)
 */
export type DammenCell = DammenPiece | null;

/**
 * 8x8 board represented as 64-element array
 * Index = row * 8 + col
 */
export type DammenBoard = DammenCell[];

/**
 * Position on board (0-63)
 */
export type BoardPosition = number;

/**
 * Row/Column coordinate
 */
export interface Coordinate {
  row: number;
  col: number;
}

/**
 * A move from one position to another
 */
export interface Move {
  from: BoardPosition;
  to: BoardPosition;
  captures?: BoardPosition[]; // Positions of captured pieces
  isJump: boolean;
  promotesToKing?: boolean;
}

/**
 * Game status
 */
export type GameStatus =
  | "waiting" // Waiting for opponent
  | "playing" // Game in progress
  | "won" // Current player won
  | "lost" // Current player lost
  | "draw" // Game ended in draw
  | "forfeit"; // Player forfeited

/**
 * AI difficulty levels
 */
export type AIDifficulty = "easy" | "medium" | "hard";

/**
 * Game state stored in session
 */
export interface DammenGameState {
  board: DammenBoard;
  currentTurn: PlayerId;
  playerAssignments: {
    [oderId: string]: PlayerId; // Maps user ID to opa/oma
  };
  moveHistory: Move[];
  capturedPieces: {
    opa: number;
    oma: number;
  };
  status: GameStatus;
  winnerId?: string | null;
  isAIGame: boolean;
  aiDifficulty?: AIDifficulty;
  mustContinueJump?: {
    from: BoardPosition;
    playerId: PlayerId;
  } | null;
  // Timer settings (optional)
  timePerMove?: number | null; // seconds, null = unlimited
  moveStartedAt?: string | null;
}

/**
 * Game result
 */
export interface DammenResult {
  winnerId: string | null;
  winnerPiece: PlayerId | null;
  isDraw: boolean;
  isForfeit: boolean;
  moveCount: number;
  capturedByOpa: number;
  capturedByOma: number;
  duration: number; // seconds
}

/**
 * Weekend mode configuration
 */
export interface DammenWeekendConfig {
  points_win: number;
  points_draw: number;
  points_participation: number;
  max_plays_per_day: number;
  allow_unlimited_plays: boolean;
  time_per_move?: number | null; // seconds, null = unlimited
}

/**
 * Game configuration (super admin settings)
 */
export interface DammenGameConfig {
  win_lottie: string;
  lose_lottie: string;
  draw_lottie: string;
  time_per_move?: number | null; // seconds for free play, null = unlimited
}

/**
 * User stats for Dammen
 */
export interface DammenUserStats {
  games_played: number;
  games_won: number;
  games_lost: number;
  games_drawn: number;
  win_rate: number;
  current_streak: number;
  longest_streak: number;
  // Per difficulty (solo mode)
  vs_ai_easy: { played: number; won: number };
  vs_ai_medium: { played: number; won: number };
  vs_ai_hard: { played: number; won: number };
  // Versus stats
  vs_human: { played: number; won: number; lost: number; drawn: number };
}

/**
 * Leaderboard entry
 */
export interface DammenLeaderboardEntry {
  rank: number;
  userId: string;
  userName: string;
  avatarUrl: string | null;
  gamesPlayed: number;
  gamesWon: number;
  winRate: number;
}

// ===== Constants =====

/**
 * Empty board (starting position will be set by initializeBoard)
 */
export const EMPTY_BOARD: DammenBoard = Array(64).fill(null);

/**
 * Default game configuration
 */
export const DEFAULT_DAMMEN_GAME_CONFIG: DammenGameConfig = {
  win_lottie: "trophy",
  lose_lottie: "Loudly_Crying_Emoji",
  draw_lottie: "Nerd_Emoji",
  time_per_move: null, // unlimited
};

/**
 * Default weekend configuration
 */
export const DEFAULT_DAMMEN_WEEKEND_CONFIG: DammenWeekendConfig = {
  points_win: 30,
  points_draw: 10,
  points_participation: 5,
  max_plays_per_day: 10,
  allow_unlimited_plays: false,
  time_per_move: null,
};

/**
 * Empty user stats
 */
export const EMPTY_DAMMEN_STATS: DammenUserStats = {
  games_played: 0,
  games_won: 0,
  games_lost: 0,
  games_drawn: 0,
  win_rate: 0,
  current_streak: 0,
  longest_streak: 0,
  vs_ai_easy: { played: 0, won: 0 },
  vs_ai_medium: { played: 0, won: 0 },
  vs_ai_hard: { played: 0, won: 0 },
  vs_human: { played: 0, won: 0, lost: 0, drawn: 0 },
};

/**
 * Character SVG paths
 */
export const CHARACTER_SVGS: Record<PlayerId, string> = {
  opa: "/assets/games/opa_endless_runner.svg",
  oma: "/assets/games/omam_endless_runner.svg",
};

/**
 * Character colors
 */
export const CHARACTER_COLORS: Record<PlayerId, string> = {
  opa: "#3B82F6", // Blue
  oma: "#EC4899", // Pink
};

/**
 * Helper: Convert position to row/col
 */
export const positionToCoord = (pos: BoardPosition): Coordinate => ({
  row: Math.floor(pos / 8),
  col: pos % 8,
});

/**
 * Helper: Convert row/col to position
 */
export const coordToPosition = (row: number, col: number): BoardPosition =>
  row * 8 + col;

/**
 * Helper: Check if position is on dark square (playable)
 */
export const isDarkSquare = (pos: BoardPosition): boolean => {
  const { row, col } = positionToCoord(pos);
  return (row + col) % 2 === 1;
};

/**
 * Helper: Check if position is valid (0-63)
 */
export const isValidPosition = (pos: number): pos is BoardPosition =>
  pos >= 0 && pos < 64;

/**
 * Initialize the board with starting pieces
 * Pieces are placed on dark squares only
 */
export const initializeBoard = (): DammenBoard => {
  const board: DammenBoard = Array(64).fill(null);

  // Place Oma pieces (top 3 rows, dark squares)
  for (let row = 0; row < 3; row++) {
    for (let col = 0; col < 8; col++) {
      const pos = coordToPosition(row, col);
      if (isDarkSquare(pos)) {
        board[pos] = { owner: "oma", type: "normal" };
      }
    }
  }

  // Place Opa pieces (bottom 3 rows, dark squares)
  for (let row = 5; row < 8; row++) {
    for (let col = 0; col < 8; col++) {
      const pos = coordToPosition(row, col);
      if (isDarkSquare(pos)) {
        board[pos] = { owner: "opa", type: "normal" };
      }
    }
  }

  return board;
};

